import { useEffect, useMemo, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
	AlertTriangle,
	ChevronDown,
	Database,
	ExternalLink,
	FileText,
	Globe,
	Loader2,
	Pencil,
	Plus,
	Save,
	Sparkles,
	Trash2,
	Type,
	Upload,
	X,
	CheckCircle2,
	ArrowRight,
} from 'lucide-react'
import { Link, useNavigate } from 'react-router-dom'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from '@/components/ui/collapsible'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from '@/components/ui/tooltip'
import { useToast } from '@/components/ui/use-toast'
import { useProgramContext } from '@/context/ProgramContext'
import { useTeamContext } from '@/context/TeamContext'
import { supabase } from '@/integrations/supabase/client'
import { getApiBaseUrl } from '@/lib/api'
import { getAuthHeaders } from '@/services/api'

type KnowledgeDocument = {
	id: string
	title: string
	file_name: string | null
	source_type: 'file' | 'text' | 'url'
	source_url: string | null
	raw_text: string | null
	status: 'processing' | 'ready' | 'failed' | string
	chunk_count: number | null
	error_message: string | null
	metadata: { crawled_pages?: Array<{ url: string; title: string | null }> } | null
	created_at: string
	updated_at: string
}

type KnowledgeChunk = {
	id: string
	chunk_index: number
	content: string
}

type AiAssistantStatus = {
	enabled: boolean
	canGenerateNow: boolean
	aiAnswersUsed: number
	aiAnswersLimit: number
	legacyAddon: boolean
	openAIConfigured: boolean
	docsReadyCount: number
	isPremium: boolean
}

type AiSettings = {
	id: string
	ai_custom_instructions: string | null
}

const API_URL = getApiBaseUrl()

function getSourceUrlValidationError(input: string) {
	try {
		const parsed = new URL(input)

		if (!['http:', 'https:'].includes(parsed.protocol)) {
			return 'Use a full http:// or https:// URL.'
		}

		if (
			parsed.hostname === 'localhost' ||
			parsed.hostname === '127.0.0.1' ||
			parsed.hostname.endsWith('.localhost')
		) {
			return 'Localhost URLs cannot be added to the knowledge base.'
		}

		return null
	} catch {
		return 'Enter a valid URL, including http:// or https://.'
	}
}

function formatSourceType(sourceType: KnowledgeDocument['source_type']) {
	switch (sourceType) {
		case 'file':
			return 'File'
		case 'text':
			return 'Text'
		case 'url':
			return 'URL'
		default:
			return sourceType
	}
}

function formatStatusLabel(status: KnowledgeDocument['status']) {
	switch (status) {
		case 'ready':
			return 'Ready'
		case 'processing':
			return 'Processing'
		case 'failed':
			return 'Failed'
		default:
			return status
	}
}

function getStatusBadgeClassName(status: KnowledgeDocument['status']) {
	switch (status) {
		case 'ready':
			return 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100'
		case 'processing':
			return 'bg-amber-100 text-amber-700 hover:bg-amber-100'
		case 'failed':
			return 'bg-red-100 text-red-700 hover:bg-red-100'
		default:
			return 'bg-slate-100 text-slate-700 hover:bg-slate-100'
	}
}

function getQuotaLabel(status?: AiAssistantStatus) {
	if (!status) return 'Loading...'
	if (status.legacyAddon) return 'Legacy add-on'
	if (status.aiAnswersLimit === -1) return 'Unlimited'
	if (status.aiAnswersLimit === 0) return 'Not included on current plan'
	return `${status.aiAnswersUsed}/${status.aiAnswersLimit} used this month`
}

function getSourceIcon(sourceType: KnowledgeDocument['source_type']) {
	switch (sourceType) {
		case 'url':
			return Globe
		case 'text':
			return Type
		default:
			return FileText
	}
}

function StatusDot({ active }: { active: boolean }) {
	return (
		<span
			className={`inline-block h-2 w-2 shrink-0 rounded-full ${
				active ? 'bg-emerald-500' : 'bg-amber-500'
			}`}
		/>
	)
}

async function getUploadAuthHeader() {
	const {
		data: { session },
	} = await supabase.auth.getSession()

	return session?.access_token
		? { Authorization: `Bearer ${session.access_token}` }
		: undefined
}

function DocumentDetailPanel({
	documentId,
	teamId,
	canWrite,
	onClose,
	onUpdated,
}: {
	documentId: string
	teamId: string
	canWrite: boolean
	onClose: () => void
	onUpdated: () => void
}) {
	const { toast } = useToast()
	const [isEditing, setIsEditing] = useState(false)
	const [editTitle, setEditTitle] = useState('')
	const [editContent, setEditContent] = useState('')
	const [isSaving, setIsSaving] = useState(false)

	const { data, isLoading } = useQuery<{
		document: KnowledgeDocument
		chunks: KnowledgeChunk[]
	}>({
		queryKey: ['knowledge-document-detail', documentId, teamId],
		queryFn: async () => {
			const response = await fetch(
				`${API_URL}/api/ai-assistant/documents/${documentId}?teamId=${teamId}`,
				{ headers: await getAuthHeaders() }
			)
			if (!response.ok) throw new Error('Failed to load document')
			return response.json()
		},
		enabled: Boolean(documentId && teamId),
	})

	const doc = data?.document
	const chunks = data?.chunks || []
	const crawledPages = doc?.metadata?.crawled_pages || []

	useEffect(() => {
		if (doc) {
			setEditTitle(doc.title)
			setEditContent(doc.raw_text || chunks.map((c) => c.content).join('\n\n'))
		}
	}, [doc, chunks])

	const handleSave = async () => {
		if (!doc) return
		try {
			setIsSaving(true)
			const body: Record<string, string> = { title: editTitle.trim() }
			if (doc.source_type === 'text') {
				body.content = editContent.trim()
			}
			const response = await fetch(
				`${API_URL}/api/ai-assistant/documents/${documentId}`,
				{
					method: 'PUT',
					headers: await getAuthHeaders(),
					body: JSON.stringify(body),
				}
			)
			if (!response.ok) {
				const err = await response.json().catch(() => ({}))
				throw new Error(err.error || 'Failed to update')
			}
			setIsEditing(false)
			onUpdated()
			toast({ title: 'Document updated' })
		} catch (error) {
			toast({
				variant: 'destructive',
				title: 'Update failed',
				description: error instanceof Error ? error.message : 'Failed to update',
			})
		} finally {
			setIsSaving(false)
		}
	}

	const SourceIcon = doc ? getSourceIcon(doc.source_type) : FileText

	return (
		<Dialog open onOpenChange={(open) => !open && onClose()}>
			<DialogContent className="sm:max-w-2xl max-h-[85vh] flex flex-col p-0 gap-0">
				{/* Header */}
				<div className="px-6 pt-6 pb-4 border-b shrink-0">
					{isLoading ? (
						<Skeleton className="h-6 w-2/3" />
					) : (
						<>
							<div className="flex items-start gap-3">
								<div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted">
									<SourceIcon className="h-4 w-4 text-muted-foreground" />
								</div>
								<div className="flex-1 min-w-0 pr-6">
									{isEditing ? (
										<Input
											value={editTitle}
											onChange={(e) => setEditTitle(e.target.value)}
											className="font-semibold text-lg"
										/>
									) : (
										<DialogTitle className="text-lg leading-tight">
											{doc?.title}
										</DialogTitle>
									)}
									<div className="flex items-center gap-2 mt-1.5">
										{doc && (
											<Badge className={getStatusBadgeClassName(doc.status)}>
												{formatStatusLabel(doc.status)}
											</Badge>
										)}
										{doc && (
											<span className="text-xs text-muted-foreground">
												{formatSourceType(doc.source_type)}
												{doc.chunk_count ? ` / ${doc.chunk_count} chunks` : ''}
											</span>
										)}
										{doc && (
											<span className="text-xs text-muted-foreground ml-auto">
												Updated {new Date(doc.updated_at).toLocaleDateString()}
											</span>
										)}
									</div>
								</div>
							</div>
							<DialogDescription className="sr-only">
								Knowledge source details
							</DialogDescription>
						</>
					)}
				</div>

				{/* Scrollable body */}
				<div className="flex-1 overflow-y-auto px-6 py-5">
					{isLoading ? (
						<div className="space-y-4">
							{[1, 2, 3].map((i) => (
								<Skeleton key={i} className="h-20 w-full" />
							))}
						</div>
					) : doc ? (
						<div className="space-y-5">
							{/* Source URL */}
							{doc.source_url && (
								<div className="space-y-1">
									<p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
										Source URL
									</p>
									<a
										href={doc.source_url}
										target="_blank"
										rel="noreferrer"
										className="text-sm text-primary underline underline-offset-2 inline-flex items-center gap-1 break-all"
									>
										{doc.source_url}
										<ExternalLink className="h-3 w-3 shrink-0" />
									</a>
								</div>
							)}

							{/* Crawled Pages */}
							{crawledPages.length > 1 && (
								<div className="space-y-2">
									<p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
										Crawled Pages ({crawledPages.length})
									</p>
									<div className="rounded-lg border divide-y max-h-48 overflow-y-auto">
										{crawledPages.map((page, i) => (
											<a
												key={i}
												href={page.url}
												target="_blank"
												rel="noreferrer"
												className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-muted/50 transition-colors"
											>
												<Globe className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
												<span className="truncate flex-1">
													{page.title || page.url}
												</span>
												<ExternalLink className="h-3 w-3 text-muted-foreground shrink-0" />
											</a>
										))}
									</div>
								</div>
							)}

							<Separator />

							{/* Content / Chunks */}
							<div className="space-y-3">
								<div className="flex items-center justify-between">
									<p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
										Extracted Knowledge ({chunks.length} chunks)
									</p>
									{canWrite && doc.source_type === 'text' && (
										<Button
											variant="ghost"
											size="sm"
											className="h-7 text-xs"
											onClick={() => {
												if (isEditing) {
													setIsEditing(false)
													setEditTitle(doc.title)
													setEditContent(
														doc.raw_text ||
															chunks.map((c) => c.content).join('\n\n')
													)
												} else {
													setIsEditing(true)
												}
											}}
										>
											{isEditing ? (
												<><X className="mr-1 h-3 w-3" /> Cancel</>
											) : (
												<><Pencil className="mr-1 h-3 w-3" /> Edit</>
											)}
										</Button>
									)}
								</div>

								{isEditing ? (
									<div className="space-y-3">
										<Textarea
											value={editContent}
											onChange={(e) => setEditContent(e.target.value)}
											className="min-h-[300px] text-sm font-mono"
										/>
										<Button
											onClick={handleSave}
											disabled={isSaving || !editTitle.trim()}
											size="sm"
											className="w-full"
										>
											{isSaving ? (
												<Loader2 className="mr-2 h-4 w-4 animate-spin" />
											) : (
												<Save className="mr-2 h-4 w-4" />
											)}
											Save changes
										</Button>
									</div>
								) : chunks.length > 0 ? (
									<div className="space-y-2">
										{chunks.map((chunk) => (
											<div
												key={chunk.id}
												className="rounded-lg border bg-muted/30 p-3"
											>
												<span className="text-[10px] font-medium text-muted-foreground uppercase">
													Chunk {chunk.chunk_index + 1}
												</span>
												<p className="text-sm whitespace-pre-wrap leading-relaxed mt-1">
													{chunk.content}
												</p>
											</div>
										))}
									</div>
								) : (
									<p className="text-sm text-muted-foreground">
										No chunks available yet.
									</p>
								)}
							</div>
						</div>
					) : null}
				</div>
			</DialogContent>
		</Dialog>
	)
}

export default function KnowledgeBase() {
	const { toast } = useToast()
	const queryClient = useQueryClient()
	const navigate = useNavigate()
	const { teamId } = useTeamContext()
	const { programId, currentProgram, currentProgramAccessLevel } = useProgramContext()
	const [selectedFile, setSelectedFile] = useState<File | null>(null)
	const [textTitle, setTextTitle] = useState('')
	const [textContent, setTextContent] = useState('')
	const [sourceUrl, setSourceUrl] = useState('')
	const [crawlSubpages, setCrawlSubpages] = useState(false)
	const [discoveredPages, setDiscoveredPages] = useState<
		Array<{ url: string; title: string | null }>
	>([])
	const [selectedPageUrls, setSelectedPageUrls] = useState<Set<string>>(new Set())
	const [isDiscovering, setIsDiscovering] = useState(false)
	const [pageFilter, setPageFilter] = useState('')
	const [customInstructions, setCustomInstructions] = useState('')
	const [isUploadingFile, setIsUploadingFile] = useState(false)
	const [isSavingText, setIsSavingText] = useState(false)
	const [isSavingUrl, setIsSavingUrl] = useState(false)
	const [isSavingInstructions, setIsSavingInstructions] = useState(false)
	const [isAddSourceOpen, setIsAddSourceOpen] = useState(false)
	const [isInstructionsOpen, setIsInstructionsOpen] = useState(false)
	const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(null)
	const isReadOnly = currentProgramAccessLevel === 'read'

	const scopedParams = useMemo(() => {
		const params = new URLSearchParams()
		if (teamId) params.set('teamId', teamId)
		if (programId) params.set('programId', programId)
		return params
	}, [programId, teamId])

	const { data: aiAssistantStatus, error: statusError, isLoading: isStatusLoading } = useQuery<AiAssistantStatus>({
		queryKey: ['knowledge-base-status', teamId, programId],
		queryFn: async () => {
			if (!teamId) {
				throw new Error('A team is required to load the knowledge base.')
			}

			const response = await fetch(
				`${API_URL}/api/ai-assistant/status?${scopedParams.toString()}`,
				{
					headers: await getAuthHeaders(),
				}
			)

			if (!response.ok) {
				throw new Error('Failed to load AI assistant status')
			}

			return response.json()
		},
		enabled: Boolean(teamId),
		staleTime: 30000,
	})

	const { data: documents = [], error: documentsError, isLoading: isLoadingDocuments } =
		useQuery<KnowledgeDocument[]>({
			queryKey: ['knowledge-base-documents', teamId, programId],
			queryFn: async () => {
				if (!teamId) {
					throw new Error('A team is required to load documents.')
				}

				const response = await fetch(
					`${API_URL}/api/ai-assistant/documents?${scopedParams.toString()}`,
					{
						headers: await getAuthHeaders(),
					}
				)

				if (!response.ok) {
					throw new Error('Failed to load knowledge base documents')
				}

				const data = await response.json()
				return data.documents || []
			},
			enabled: Boolean(teamId),
		})

	const { data: settings, error: settingsError } = useQuery<AiSettings | null>({
		queryKey: ['knowledge-base-settings', teamId, programId],
		queryFn: async () => {
			if (!teamId || !programId) {
				return null
			}

			const response = await fetch(
				`${API_URL}/api/ai-assistant/settings?${scopedParams.toString()}`,
				{
					headers: await getAuthHeaders(),
				}
			)

			if (!response.ok) {
				throw new Error('Failed to load AI instructions')
			}

			const data = await response.json()
			return data.settings || null
		},
		enabled: Boolean(teamId && programId),
	})

	useEffect(() => {
		setCustomInstructions(settings?.ai_custom_instructions || '')
	}, [settings?.ai_custom_instructions])

	// Auto-expand instructions when empty (first-time setup)
	useEffect(() => {
		if (settings !== undefined) {
			setIsInstructionsOpen(!settings?.ai_custom_instructions)
		}
	}, [settings])

	const hasEntitlement = Boolean(aiAssistantStatus?.enabled)
	const canWrite = !isReadOnly

	const refreshKnowledgeBase = async () => {
		await Promise.all([
			queryClient.invalidateQueries({
				queryKey: ['knowledge-base-documents', teamId, programId],
			}),
			queryClient.invalidateQueries({
				queryKey: ['knowledge-base-status', teamId, programId],
			}),
			queryClient.invalidateQueries({
				queryKey: ['knowledge-base-settings', teamId, programId],
			}),
		])
	}

	const handleFileUpload = async () => {
		if (!selectedFile || !teamId) return

		try {
			setIsUploadingFile(true)

			const formData = new FormData()
			formData.append('file', selectedFile)
			formData.append('teamId', teamId)
			if (programId) {
				formData.append('programId', programId)
			}

			const response = await fetch(`${API_URL}/api/ai-assistant/documents/upload`, {
				method: 'POST',
				headers: await getUploadAuthHeader(),
				body: formData,
			})

			if (!response.ok) {
				const error = await response.json().catch(() => ({}))
				throw new Error(error.error || 'Failed to upload document')
			}

			setSelectedFile(null)
			setIsAddSourceOpen(false)
			await refreshKnowledgeBase()
			toast({
				title: 'Document uploaded',
				description: 'The knowledge base is processing your file now.',
			})
		} catch (error) {
			toast({
				variant: 'destructive',
				title: 'Upload failed',
				description:
					error instanceof Error ? error.message : 'Failed to upload document',
			})
		} finally {
			setIsUploadingFile(false)
		}
	}

	const handleSaveText = async () => {
		if (!teamId || !textTitle.trim() || !textContent.trim()) return

		try {
			setIsSavingText(true)
			const response = await fetch(`${API_URL}/api/ai-assistant/documents/text`, {
				method: 'POST',
				headers: await getAuthHeaders(),
				body: JSON.stringify({
					teamId,
					programId,
					title: textTitle.trim(),
					content: textContent.trim(),
				}),
			})

			if (!response.ok) {
				const error = await response.json().catch(() => ({}))
				throw new Error(error.error || 'Failed to save text document')
			}

			setTextTitle('')
			setTextContent('')
			setIsAddSourceOpen(false)
			await refreshKnowledgeBase()
			toast({
				title: 'Text saved',
				description: 'Your note is now part of the AI knowledge base.',
			})
		} catch (error) {
			toast({
				variant: 'destructive',
				title: 'Could not save text',
				description:
					error instanceof Error ? error.message : 'Failed to save text document',
			})
		} finally {
			setIsSavingText(false)
		}
	}

	const handleDiscoverUrl = async () => {
		const trimmedUrl = sourceUrl.trim()
		if (!teamId || !trimmedUrl) return

		const validationError = getSourceUrlValidationError(trimmedUrl)
		if (validationError) {
			toast({
				variant: 'destructive',
				title: 'Could not scan URL',
				description: validationError,
			})
			return
		}

		try {
			setIsDiscovering(true)
			setDiscoveredPages([])
			setSelectedPageUrls(new Set())
			setPageFilter('')

			if (!crawlSubpages) {
				// Single page — skip discovery, go straight to crawl
				await handleCrawlSelected(trimmedUrl, [trimmedUrl])
				return
			}

			const response = await fetch(`${API_URL}/api/ai-assistant/discover-url`, {
				method: 'POST',
				headers: await getAuthHeaders(),
				body: JSON.stringify({ teamId, url: trimmedUrl }),
			})

			const result = await response.json().catch(() => ({}))
			if (!response.ok) {
				throw new Error(result.error || 'Failed to discover pages')
			}

			const pages = result.pages || []
			setDiscoveredPages(pages)
			// Pre-select the root page
			setSelectedPageUrls(new Set(pages.length > 0 ? [pages[0].url] : []))
		} catch (error) {
			toast({
				variant: 'destructive',
				title: 'Could not scan URL',
				description:
					error instanceof Error ? error.message : 'Failed to discover pages',
			})
		} finally {
			setIsDiscovering(false)
		}
	}

	const handleCrawlSelected = async (rootUrl: string, urls: string[]) => {
		if (!teamId) return

		try {
			setIsSavingUrl(true)
			const response = await fetch(`${API_URL}/api/ai-assistant/documents/url`, {
				method: 'POST',
				headers: await getAuthHeaders(),
				body: JSON.stringify({
					teamId,
					programId,
					url: rootUrl,
					selectedUrls: urls,
				}),
			})

			const result = await response.json().catch(() => ({}))
			if (!response.ok) {
				throw new Error(result.error || 'Failed to save URL source')
			}

			const pageCount = result.pages?.length || result.crawledUrls?.length || 1

			setSourceUrl('')
			setCrawlSubpages(false)
			setDiscoveredPages([])
			setSelectedPageUrls(new Set())
			setIsAddSourceOpen(false)
			await refreshKnowledgeBase()
			toast({
				title: 'URL added',
				description:
					pageCount > 1
						? `Crawled and indexed ${pageCount} pages from that site.`
						: 'The page has been crawled and indexed.',
			})
		} catch (error) {
			toast({
				variant: 'destructive',
				title: 'Could not add URL',
				description:
					error instanceof Error ? error.message : 'Failed to save URL source',
			})
		} finally {
			setIsSavingUrl(false)
		}
	}

	const handleDeleteDocument = async (documentId: string) => {
		if (!teamId) return

		try {
			const response = await fetch(
				`${API_URL}/api/ai-assistant/documents/${documentId}?${scopedParams.toString()}`,
				{
					method: 'DELETE',
					headers: await getAuthHeaders(),
				}
			)

			if (!response.ok) {
				const error = await response.json().catch(() => ({}))
				throw new Error(error.error || 'Failed to delete document')
			}

			await refreshKnowledgeBase()
			toast({
				title: 'Document deleted',
				description: 'It has been removed from the knowledge base.',
			})
		} catch (error) {
			toast({
				variant: 'destructive',
				title: 'Could not delete document',
				description:
					error instanceof Error ? error.message : 'Failed to delete document',
			})
		}
	}

	const handleSaveInstructions = async () => {
		if (!teamId || !programId) return

		try {
			setIsSavingInstructions(true)
			const response = await fetch(`${API_URL}/api/ai-assistant/settings`, {
				method: 'PUT',
				headers: await getAuthHeaders(),
				body: JSON.stringify({
					teamId,
					programId,
					customInstructions: customInstructions.trim() || null,
				}),
			})

			if (!response.ok) {
				const error = await response.json().catch(() => ({}))
				throw new Error(error.error || 'Failed to save AI instructions')
			}

			await refreshKnowledgeBase()
			toast({
				title: 'Instructions saved',
				description: 'The assistant will use these instructions for this project.',
			})
		} catch (error) {
			toast({
				variant: 'destructive',
				title: 'Could not save instructions',
				description:
					error instanceof Error ? error.message : 'Failed to save AI instructions',
			})
		} finally {
			setIsSavingInstructions(false)
		}
	}

	// Build consolidated notices
	const notices: Array<{
		variant: 'destructive' | 'default'
		icon: typeof AlertTriangle
		title: string
		description: React.ReactNode
	}> = []

	if (statusError instanceof Error) {
		notices.push({
			variant: 'destructive',
			icon: AlertTriangle,
			title: 'Could not load AI status',
			description: statusError.message,
		})
	}
	if (documentsError instanceof Error) {
		notices.push({
			variant: 'destructive',
			icon: AlertTriangle,
			title: 'Could not load documents',
			description: documentsError.message,
		})
	}
	if (settingsError instanceof Error) {
		notices.push({
			variant: 'destructive',
			icon: AlertTriangle,
			title: 'Could not load instructions',
			description: settingsError.message,
		})
	}
	if (!hasEntitlement) {
		notices.push({
			variant: 'default',
			icon: Sparkles,
			title: 'AI Reply Assistant is not enabled for this team',
			description: (
				<>
					You can browse this page, but adding new AI sources may be blocked until
					the team upgrades. Open{' '}
					<Link to="/settings?tab=billing" className="underline underline-offset-2">
						Billing
					</Link>{' '}
					to enable AI access.
				</>
			),
		})
	}
	if (isReadOnly) {
		notices.push({
			variant: 'default',
			icon: AlertTriangle,
			title: 'Read-only project access',
			description:
				'You can view this knowledge base, but document changes and instruction updates are disabled for your current access level.',
		})
	}

	const hasInstructions = Boolean(settings?.ai_custom_instructions?.trim())

	return (
		<div className="p-4 md:p-8 max-w-6xl mx-auto space-y-6">
			{/* Section 1: Page Header */}
			<div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
				<div className="space-y-2">
					<div className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
						<Database className="h-5 w-5" />
					</div>
					<div>
						<h1 className="text-2xl font-semibold tracking-tight">AI Knowledge Base</h1>
						<p className="text-sm text-muted-foreground">
							Manage the sources and instructions used by AI Reply Assistant for{' '}
							<span className="font-medium text-foreground">
								{currentProgram?.name || 'the selected project'}
							</span>
							.
						</p>
					</div>
				</div>
				<Badge variant="outline">{getQuotaLabel(aiAssistantStatus)}</Badge>
			</div>

			{isStatusLoading ? (
				<div className="flex justify-center py-20">
					<Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
				</div>
			) : !hasEntitlement ? (
				<Card className="max-w-lg mx-auto mt-8 border-slate-200 bg-gradient-to-br from-slate-50 to-purple-50/50 dark:border-slate-800 dark:from-slate-950 dark:to-purple-950/50">
					<CardHeader className="text-center pb-4">
						<div className="mx-auto mb-3 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10">
							<Sparkles className="h-7 w-7 text-primary" />
						</div>
						<CardTitle className="text-xl">Unlock AI Knowledge Base</CardTitle>
						<CardDescription className="text-sm leading-relaxed">
							Train AI Reply Assistant with your product docs, FAQs, and custom
							instructions so it can draft accurate replies to customer feedback
							automatically.
						</CardDescription>
					</CardHeader>
					<CardContent className="space-y-5">
						<ul className="space-y-3 text-sm">
							{[
								'200 AI-generated reply drafts per month',
								'Upload documents, paste text, or crawl URLs',
								'Auto-learns from your conversations',
								'Citations from your knowledge sources',
							].map((feature) => (
								<li key={feature} className="flex items-start gap-2.5">
									<CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0 text-primary" />
									<span>{feature}</span>
								</li>
							))}
						</ul>
						<Button
							className="w-full"
							onClick={() => navigate('/settings?tab=billing')}
						>
							Upgrade to Growth
							<ArrowRight className="h-4 w-4 ml-2" />
						</Button>
					</CardContent>
				</Card>
			) : (
			<>
			{/* Section 2: Compact Status Bar */}
			<TooltipProvider>
				<div className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-lg border bg-card px-4 py-3 text-sm">
					<Tooltip>
						<TooltipTrigger asChild>
							<div className="flex items-center gap-2 cursor-default">
								<StatusDot active={Boolean(aiAssistantStatus?.enabled)} />
								<span className="text-muted-foreground">AI Access</span>
								<span className="font-medium">
									{aiAssistantStatus?.enabled ? 'Enabled' : 'Upgrade needed'}
								</span>
							</div>
						</TooltipTrigger>
						<TooltipContent side="bottom" className="max-w-xs">
							{aiAssistantStatus?.enabled
								? 'This project can use AI Reply Assistant and its knowledge sources.'
								: 'Adding new AI sources may be blocked until the team upgrades.'}
						</TooltipContent>
					</Tooltip>

					<Separator orientation="vertical" className="hidden sm:block h-4" />

					<Tooltip>
						<TooltipTrigger asChild>
							<div className="flex items-center gap-2 cursor-default">
								<span className="inline-block h-2 w-2 shrink-0 rounded-full bg-primary/60" />
								<span className="text-muted-foreground">Sources</span>
								<span className="font-medium">
									{aiAssistantStatus?.docsReadyCount ?? 0} ready
								</span>
								{documents.length > 0 && (
									<span className="text-muted-foreground">
										/ {documents.length} total
									</span>
								)}
							</div>
						</TooltipTrigger>
						<TooltipContent side="bottom" className="max-w-xs">
							{documents.length} document{documents.length === 1 ? '' : 's'} indexed
							for the current project.
						</TooltipContent>
					</Tooltip>

				</div>
			</TooltipProvider>

			{/* Section 3: Consolidated Alerts */}
			{notices.length > 0 && (
				<div className="space-y-2">
					{notices.length === 1 ? (
						(() => {
							const FirstIcon = notices[0].icon
							return (
								<Alert variant={notices[0].variant}>
									<FirstIcon className="h-4 w-4" />
									<AlertTitle>{notices[0].title}</AlertTitle>
									<AlertDescription>{notices[0].description}</AlertDescription>
								</Alert>
							)
						})()
					) : (
						<Collapsible>
							{(() => {
								const FirstIcon = notices[0].icon
								return (
									<Alert variant={notices[0].variant}>
										<FirstIcon className="h-4 w-4" />
										<AlertTitle className="flex items-center justify-between">
											{notices[0].title}
											<CollapsibleTrigger asChild>
												<Button variant="ghost" size="sm" className="h-auto px-2 py-1 text-xs">
													+{notices.length - 1} more{' '}
													{notices.length - 1 === 1 ? 'notice' : 'notices'}
												</Button>
											</CollapsibleTrigger>
										</AlertTitle>
										<AlertDescription>{notices[0].description}</AlertDescription>
									</Alert>
								)
							})()}
							<CollapsibleContent className="space-y-2">
								{notices.slice(1).map((notice, index) => {
									const NoticeIcon = notice.icon
									return (
										<Alert key={index} variant={notice.variant}>
											<NoticeIcon className="h-4 w-4" />
											<AlertTitle>{notice.title}</AlertTitle>
											<AlertDescription>{notice.description}</AlertDescription>
										</Alert>
									)
								})}
							</CollapsibleContent>
						</Collapsible>
					)}
				</div>
			)}

			{/* Section 4: Documents Table (Hero) */}
			<Card>
				<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
					<div className="space-y-1">
						<CardTitle>Knowledge Sources</CardTitle>
						<CardDescription>
							Documents indexed for{' '}
							{currentProgram?.name || 'the selected project'}
						</CardDescription>
					</div>
					<Dialog open={isAddSourceOpen} onOpenChange={setIsAddSourceOpen}>
						<Button
							onClick={() => setIsAddSourceOpen(true)}
							disabled={!canWrite}
							size="sm"
						>
							<Plus className="mr-2 h-4 w-4" />
							Add source
						</Button>
						<DialogContent className="sm:max-w-lg max-h-[85vh] flex flex-col">
							<DialogHeader>
								{discoveredPages.length > 0 ? (
									<>
										<DialogTitle>Select pages to import</DialogTitle>
										<DialogDescription>
											{discoveredPages.length} pages found on{' '}
											{(() => { try { return new URL(sourceUrl.trim()).hostname } catch { return sourceUrl.trim() } })()}
										</DialogDescription>
									</>
								) : (
									<>
										<DialogTitle>Add a knowledge source</DialogTitle>
										<DialogDescription>
											Upload files, paste internal notes, or crawl a public URL.
										</DialogDescription>
									</>
								)}
							</DialogHeader>
							{discoveredPages.length > 0 ? (
								<div className="flex flex-col gap-3 min-h-0 flex-1">
									<div className="flex items-center gap-2">
										<Input
											placeholder="Filter pages..."
											value={pageFilter}
											onChange={(e) => setPageFilter(e.target.value)}
											className="h-8 text-sm flex-1"
										/>
										<Button
											variant="outline"
											size="sm"
											className="h-8 shrink-0 text-xs"
											onClick={() => {
												setDiscoveredPages([])
												setSelectedPageUrls(new Set())
												setPageFilter('')
											}}
										>
											Change URL
										</Button>
									</div>
									<div className="flex items-center justify-between text-xs text-muted-foreground">
										<div className="flex items-center gap-2">
											<button
												type="button"
												className="text-primary hover:underline"
												onClick={() => {
													const filtered = discoveredPages
														.filter(
															(p) =>
																!pageFilter ||
																p.url.toLowerCase().includes(pageFilter.toLowerCase()) ||
																p.title?.toLowerCase().includes(pageFilter.toLowerCase())
														)
														.map((p) => p.url)
													setSelectedPageUrls(new Set(filtered))
												}}
											>
												Select all
											</button>
											<span>/</span>
											<button
												type="button"
												className="text-primary hover:underline"
												onClick={() => setSelectedPageUrls(new Set())}
											>
												Clear
											</button>
										</div>
										<span className="font-medium text-foreground">
											{selectedPageUrls.size} of{' '}
											{discoveredPages.filter(
												(p) =>
													!pageFilter ||
													p.url.toLowerCase().includes(pageFilter.toLowerCase()) ||
													p.title?.toLowerCase().includes(pageFilter.toLowerCase())
											).length}{' '}
											selected
										</span>
									</div>
									<div className="flex-1 min-h-0 overflow-y-auto rounded-lg border divide-y max-h-[40vh]">
										{discoveredPages
											.filter(
												(p) =>
													!pageFilter ||
													p.url.toLowerCase().includes(pageFilter.toLowerCase()) ||
													p.title?.toLowerCase().includes(pageFilter.toLowerCase())
											)
											.map((page) => {
												const pathname = new URL(page.url).pathname
												const isSelected = selectedPageUrls.has(page.url)
												return (
													<label
														key={page.url}
														className={`flex items-center gap-3 px-3 py-2 text-sm cursor-pointer transition-colors ${
															isSelected ? 'bg-primary/5' : 'hover:bg-muted/50'
														}`}
													>
														<Checkbox
															checked={isSelected}
															onCheckedChange={(checked) => {
																setSelectedPageUrls((prev) => {
																	const next = new Set(prev)
																	if (checked) next.add(page.url)
																	else next.delete(page.url)
																	return next
																})
															}}
														/>
														<div className="min-w-0 flex-1">
															<p className="truncate text-sm">
																{page.title || pathname}
															</p>
															{page.title && (
																<p className="truncate text-xs text-muted-foreground">
																	{pathname}
																</p>
															)}
														</div>
													</label>
												)
											})}
									</div>
									<Button
										type="button"
										className="w-full shrink-0"
										onClick={() =>
											handleCrawlSelected(sourceUrl.trim(), [...selectedPageUrls])
										}
										disabled={selectedPageUrls.size === 0 || !canWrite || isSavingUrl}
									>
										{isSavingUrl ? (
											<Loader2 className="mr-2 h-4 w-4 animate-spin" />
										) : (
											<Globe className="mr-2 h-4 w-4" />
										)}
										{isSavingUrl
											? `Crawling ${selectedPageUrls.size} pages...`
											: `Import ${selectedPageUrls.size} selected page${selectedPageUrls.size === 1 ? '' : 's'}`}
									</Button>
								</div>
							) : (
							<Tabs defaultValue="file" className="space-y-4">
								<TabsList className="grid w-full grid-cols-3">
									<TabsTrigger value="file">File</TabsTrigger>
									<TabsTrigger value="text">Text</TabsTrigger>
									<TabsTrigger value="url">URL</TabsTrigger>
								</TabsList>

								<TabsContent value="file" className="space-y-4">
									<Input
										type="file"
										onChange={(event) =>
											setSelectedFile(event.target.files?.[0] || null)
										}
										disabled={!canWrite}
									/>
									<p className="text-xs text-muted-foreground">
										PDF, markdown, plain text, docs, and similar formats are
										supported by the backend extractor.
									</p>
									<Button
										type="button"
										className="w-full"
										onClick={handleFileUpload}
										disabled={!selectedFile || !canWrite || isUploadingFile}
									>
										{isUploadingFile ? (
											<Loader2 className="mr-2 h-4 w-4 animate-spin" />
										) : (
											<Upload className="mr-2 h-4 w-4" />
										)}
										Upload file
									</Button>
								</TabsContent>

								<TabsContent value="text" className="space-y-4">
									<Input
										value={textTitle}
										onChange={(event) => setTextTitle(event.target.value)}
										placeholder="Title"
										disabled={!canWrite}
									/>
									<Textarea
										value={textContent}
										onChange={(event) => setTextContent(event.target.value)}
										placeholder="Paste product details, support policy, internal notes, or canned explanations."
										className="min-h-[140px]"
										disabled={!canWrite}
									/>
									<Button
										type="button"
										className="w-full"
										onClick={handleSaveText}
										disabled={
											!textTitle.trim() ||
											!textContent.trim() ||
											!canWrite ||
											isSavingText
										}
									>
										{isSavingText ? (
											<Loader2 className="mr-2 h-4 w-4 animate-spin" />
										) : (
											<FileText className="mr-2 h-4 w-4" />
										)}
										Save text note
									</Button>
								</TabsContent>

								<TabsContent value="url" className="space-y-4">
									<Input
										type="url"
										value={sourceUrl}
										onChange={(event) => setSourceUrl(event.target.value)}
										placeholder="https://example.com/help"
										disabled={!canWrite || isDiscovering}
									/>
									<label className="flex items-center gap-3 rounded-lg border p-3 text-sm">
										<Checkbox
											checked={crawlSubpages}
											onCheckedChange={(checked) =>
												setCrawlSubpages(Boolean(checked))
											}
											disabled={!canWrite || isDiscovering}
										/>
										<span>
											Crawl multiple pages from this site
										</span>
									</label>
									<Button
										type="button"
										className="w-full"
										onClick={handleDiscoverUrl}
										disabled={
											!sourceUrl.trim() ||
											!canWrite ||
											isDiscovering ||
											isSavingUrl
										}
									>
										{isDiscovering || isSavingUrl ? (
											<Loader2 className="mr-2 h-4 w-4 animate-spin" />
										) : (
											<Globe className="mr-2 h-4 w-4" />
										)}
										{crawlSubpages
											? isDiscovering
												? 'Scanning site...'
												: 'Scan site for pages'
											: isSavingUrl
												? 'Adding...'
												: 'Add URL source'}
									</Button>
								</TabsContent>
							</Tabs>
							)}
						</DialogContent>
					</Dialog>
				</CardHeader>
				<CardContent>
					{isLoadingDocuments ? (
						<div className="space-y-3">
							{[1, 2, 3].map((i) => (
								<div key={i} className="flex items-center gap-4">
									<Skeleton className="h-9 w-9 rounded-lg shrink-0" />
									<div className="flex-1 space-y-2">
										<Skeleton className="h-4 w-1/3" />
										<Skeleton className="h-3 w-1/5" />
									</div>
									<Skeleton className="h-6 w-16 rounded-full" />
								</div>
							))}
						</div>
					) : documents.length === 0 ? (
						<div className="rounded-xl border border-dashed px-6 py-12 text-center">
							<div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-lg bg-muted">
								<Database className="h-5 w-5 text-muted-foreground" />
							</div>
							<p className="text-sm font-medium">No sources added yet</p>
							<p className="mt-1 text-sm text-muted-foreground">
								Add a file, note, or URL and it will appear here once indexed.
							</p>
							<Button
								variant="outline"
								size="sm"
								className="mt-4"
								onClick={() => setIsAddSourceOpen(true)}
								disabled={!canWrite}
							>
								<Plus className="mr-2 h-4 w-4" />
								Add your first source
							</Button>
						</div>
					) : (
						<div className="overflow-hidden rounded-lg border border-border/50 bg-white dark:bg-slate-900">
							<Table>
								<TableHeader>
									<TableRow>
										<TableHead>Source</TableHead>
										<TableHead className="hidden sm:table-cell w-[80px]">
											Type
										</TableHead>
										<TableHead className="w-[120px]">Status</TableHead>
										<TableHead className="hidden md:table-cell w-[140px]">
											Updated
										</TableHead>
										<TableHead className="w-[60px]" />
									</TableRow>
								</TableHeader>
								<TableBody>
									{documents.map((document) => {
										const Icon = getSourceIcon(document.source_type)
										return (
											<TableRow
												key={document.id}
												className="cursor-pointer"
												onClick={() => setSelectedDocumentId(document.id)}
											>
												<TableCell>
													<div className="flex items-start gap-3">
														<div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-muted">
															<Icon className="h-4 w-4 text-muted-foreground" />
														</div>
														<div className="min-w-0">
															<p className="font-medium truncate">
																{document.title}
															</p>
															{document.file_name && (
																<p className="text-xs text-muted-foreground truncate">
																	{document.file_name}
																</p>
															)}
															{document.source_url && (
																<a
																	href={document.source_url}
																	target="_blank"
																	rel="noreferrer"
																	className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2 truncate block"
																>
																	{document.source_url}
																</a>
															)}
															{document.error_message && (
																<p className="text-xs text-red-600 mt-0.5">
																	{document.error_message}
																</p>
															)}
														</div>
													</div>
												</TableCell>
												<TableCell className="hidden sm:table-cell">
													<span className="text-sm text-muted-foreground">
														{formatSourceType(document.source_type)}
													</span>
												</TableCell>
												<TableCell>
													<div className="flex flex-col gap-1">
														<Badge
															className={getStatusBadgeClassName(
																document.status
															)}
														>
															{formatStatusLabel(document.status)}
														</Badge>
														{typeof document.chunk_count ===
															'number' && (
															<span className="text-xs text-muted-foreground">
																{document.chunk_count} chunks
															</span>
														)}
													</div>
												</TableCell>
												<TableCell className="hidden md:table-cell">
													<span className="text-sm text-muted-foreground">
														{new Date(
															document.updated_at
														).toLocaleDateString()}
													</span>
												</TableCell>
												<TableCell>
													<TooltipProvider>
														<Tooltip>
															<TooltipTrigger asChild>
																<Button
																	type="button"
																	variant="ghost"
																	size="icon"
																	className="h-8 w-8 text-muted-foreground hover:text-destructive"
																	onClick={(e) => {
																		e.stopPropagation()
																		handleDeleteDocument(
																			document.id
																		)
																	}}
																	disabled={!canWrite}
																>
																	<Trash2 className="h-4 w-4" />
																</Button>
															</TooltipTrigger>
															<TooltipContent>Delete</TooltipContent>
														</Tooltip>
													</TooltipProvider>
												</TableCell>
											</TableRow>
										)
									})}
								</TableBody>
							</Table>
						</div>
					)}
				</CardContent>
			</Card>

			{/* Section 5: Collapsible Instructions */}
			<Collapsible open={isInstructionsOpen} onOpenChange={setIsInstructionsOpen}>
				<Card>
					<CollapsibleTrigger asChild>
						<CardHeader className="cursor-pointer select-none hover:bg-muted/50 transition-colors rounded-t-lg">
							<div className="flex items-center justify-between">
								<div className="flex items-center gap-3">
									<CardTitle className="text-base">
										Project Instructions
									</CardTitle>
									{hasInstructions && (
										<Badge
											variant="outline"
											className="bg-emerald-50 text-emerald-700 border-emerald-200"
										>
											Configured
										</Badge>
									)}
								</div>
								<ChevronDown
									className={`h-4 w-4 text-muted-foreground transition-transform ${
										isInstructionsOpen ? 'rotate-180' : ''
									}`}
								/>
							</div>
							<CardDescription>
								Guide how AI drafts should sound or behave for this project.
							</CardDescription>
						</CardHeader>
					</CollapsibleTrigger>
					<CollapsibleContent>
						<CardContent className="space-y-4 pt-0">
							<Textarea
								value={customInstructions}
								onChange={(event) =>
									setCustomInstructions(event.target.value)
								}
								placeholder="Example: Keep replies concise, mention expected timelines, and offer a follow-up question when the issue is unclear."
								className="min-h-[160px]"
								disabled={!programId || !canWrite}
							/>
							<div className="flex items-center justify-between gap-3">
								<p className="text-xs text-muted-foreground">
									Saved per project. Switch the project in the sidebar to manage
									a different knowledge base.
								</p>
								<Button
									type="button"
									onClick={handleSaveInstructions}
									disabled={
										!programId || !canWrite || isSavingInstructions
									}
									size="sm"
								>
									{isSavingInstructions ? (
										<Loader2 className="mr-2 h-4 w-4 animate-spin" />
									) : (
										<Save className="mr-2 h-4 w-4" />
									)}
									Save instructions
								</Button>
							</div>
						</CardContent>
					</CollapsibleContent>
				</Card>
			</Collapsible>

			{/* Document Detail Panel */}
			{selectedDocumentId && teamId && (
				<DocumentDetailPanel
					documentId={selectedDocumentId}
					teamId={teamId}
					canWrite={canWrite}
					onClose={() => setSelectedDocumentId(null)}
					onUpdated={async () => {
						await refreshKnowledgeBase()
						queryClient.invalidateQueries({
							queryKey: ['knowledge-document-detail', selectedDocumentId, teamId],
						})
					}}
				/>
			)}
			</>
			)}
		</div>
	)
}
